import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar as CalendarIcon } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { api } from "../lib/api";

const formatDate = (iso) => {
    try {
        return new Date(iso).toLocaleDateString("en-IN", {
            day: "numeric",
            month: "long",
            year: "numeric",
        });
    } catch {
        return iso;
    }
};

export default function Blog() {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        api.get("/blog")
            .then((r) => setPosts(r.data))
            .finally(() => setLoading(false));
    }, []);

    const [featured, ...rest] = posts;

    return (
        <div className="bg-[#FDFAF5] min-h-screen">
            <Navbar />
            <section className="pt-32 md:pt-40 pb-16 bg-[#F3EFE9]/40">
                <div className="max-w-5xl mx-auto px-6 md:px-12">
                    <div className="flex items-center gap-3 mb-4">
                        <span className="ornament" />
                        <span className="text-xs tracking-[0.25em] uppercase text-[#1A5C53] font-medium">
                            Field Notes
                        </span>
                    </div>
                    <h1 className="font-serif text-5xl md:text-6xl text-[#0D1B1A] leading-tight">
                        Stories, strategies &
                        <span className="italic text-[#1A5C53]">
                            {" "}
                            debt-free wisdom.
                        </span>
                    </h1>
                    <p className="mt-6 text-[#4A5A58] max-w-2xl">
                        Real insights from 20+ years of finance — no jargon,
                        no sales pitch, just what actually works.
                    </p>
                </div>
            </section>

            <section className="py-16 md:py-20">
                <div
                    data-testid="blog-list"
                    className="max-w-5xl mx-auto px-6 md:px-12"
                >
                    {loading && (
                        <p className="text-[#758684]">Loading posts…</p>
                    )}
                    {!loading && posts.length === 0 && (
                        <div className="text-center py-20 border border-dashed border-[#E6DFD3] rounded-2xl">
                            <p className="font-serif text-2xl text-[#0D1B1A]">
                                First post coming soon.
                            </p>
                            <p className="text-[#758684] mt-2">
                                We're stocking the library. Check back shortly.
                            </p>
                        </div>
                    )}

                    {featured && (
                        <Link
                            to={`/blog/${featured.slug}`}
                            data-testid={`blog-card-${featured.slug}`}
                            className="group block bg-white border border-[#E6DFD3] rounded-2xl overflow-hidden hover:shadow-[0_12px_48px_rgba(13,27,26,0.08)] transition-shadow duration-300"
                        >
                            <div className="grid md:grid-cols-2 gap-0">
                                <div className="aspect-[4/3] md:aspect-auto bg-[#F3EFE9] overflow-hidden">
                                    {featured.cover_image_url ? (
                                        <img
                                            src={featured.cover_image_url}
                                            alt={featured.title}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                        />
                                    ) : (
                                        <div className="w-full h-full bg-gradient-to-br from-[#1A5C53]/10 to-[#D4944C]/10 flex items-center justify-center">
                                            <span className="font-serif text-6xl text-[#1A5C53]/40">
                                                D
                                            </span>
                                        </div>
                                    )}
                                </div>
                                <div className="p-8 md:p-10 flex flex-col justify-center">
                                    <div className="text-xs tracking-[0.25em] uppercase text-[#D4944C] mb-3">
                                        Latest
                                    </div>
                                    <h2 className="font-serif text-3xl md:text-4xl text-[#0D1B1A] leading-tight group-hover:text-[#1A5C53] transition-colors">
                                        {featured.title}
                                    </h2>
                                    <p className="mt-4 text-[#4A5A58]">
                                        {featured.excerpt}
                                    </p>
                                    <div className="mt-6 flex items-center gap-4 text-xs text-[#758684]">
                                        <div className="inline-flex items-center gap-1.5">
                                            <CalendarIcon size={13} />
                                            {formatDate(featured.created_at)}
                                        </div>
                                        {featured.tags?.[0] && (
                                            <span className="bg-[#F3EFE9] px-2 py-1 rounded-full">
                                                {featured.tags[0]}
                                            </span>
                                        )}
                                    </div>
                                    <div className="mt-6 inline-flex items-center gap-2 text-[#1A5C53] font-medium text-sm">
                                        Read essay{" "}
                                        <ArrowRight
                                            size={14}
                                            className="group-hover:translate-x-1 transition-transform"
                                        />
                                    </div>
                                </div>
                            </div>
                        </Link>
                    )}

                    {rest.length > 0 && (
                        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {rest.map((p) => (
                                <Link
                                    key={p.id}
                                    to={`/blog/${p.slug}`}
                                    data-testid={`blog-card-${p.slug}`}
                                    className="group bg-white border border-[#E6DFD3] rounded-2xl overflow-hidden hover:shadow-[0_12px_48px_rgba(13,27,26,0.06)] transition-shadow flex flex-col"
                                >
                                    <div className="aspect-[16/10] bg-[#F3EFE9] overflow-hidden">
                                        {p.cover_image_url ? (
                                            <img
                                                src={p.cover_image_url}
                                                alt={p.title}
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                            />
                                        ) : (
                                            <div className="w-full h-full bg-gradient-to-br from-[#1A5C53]/10 to-[#D4944C]/10" />
                                        )}
                                    </div>
                                    <div className="p-6 flex-1 flex flex-col">
                                        <h3 className="font-serif text-xl text-[#0D1B1A] leading-snug group-hover:text-[#1A5C53] transition-colors">
                                            {p.title}
                                        </h3>
                                        <p className="text-sm text-[#4A5A58] mt-3 line-clamp-3">
                                            {p.excerpt}
                                        </p>
                                        <div className="mt-4 text-xs text-[#758684] inline-flex items-center gap-1.5">
                                            <CalendarIcon size={13} />
                                            {formatDate(p.created_at)}
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            <Footer />
        </div>
    );
}
